import { Link, useRouterState } from "@tanstack/react-router";
import { CityLink } from "@/components/CityLink";
import { useState, useEffect, useRef, type ReactNode } from "react";
import { SITE_NAME, PAKISTAN_CITIES, INDIA_CITIES } from "@/lib/gold-data";
import logoUrl from "@/assets/logo-144.webp";
import { ThemeToggle } from "@/components/ThemeToggle";
import { DisplayAd, ensureGptScript, refreshOutOfPageAds } from "@/components/DisplayAd";
import { SearchAutocomplete } from "@/components/SearchAutocomplete";
import { FooterCrawlerLinks } from "@/components/FooterCrawlerLinks";

type NavLink = { to: string; label: string };
type NavDropdown = { label: string; children: NavLink[] };
type NavItem = NavLink | NavDropdown;

const isDropdown = (i: NavItem): i is NavDropdown => "children" in i;

const NAV: NavItem[] = [
  { to: "/", label: "Home" },
  {
    label: "🥇 Check Gold Price",
    children: [
      { to: "/pakistan", label: "🇵🇰 Gold Price in Pakistan — پاکستان میں سونے کی قیمت" },
      { to: "/india", label: "🇮🇳 Gold Price in India — भारत में सोने का भाव" },
      { to: "/uae", label: "🇦🇪 Gold Price in UAE — أسعار الذهب في الإمارات" },
      { to: "/ar/gold-rate-saudi-arabia", label: "🇸🇦 Gold Price in Saudi Arabia — أسعار الذهب في السعودية" },
    ],
  },
  {
    label: "🥈 Check Silver Price",
    children: [
      { to: "/silver-rate-pakistan", label: "🇵🇰 Silver Price in Pakistan — پاکستان میں چاندی کی قیمت" },
      { to: "/silver-rate-india", label: "🇮🇳 Silver Price in India — भारत में चांदी का भाव" },
    ],
  },
  { to: "/age-calculator", label: "🎂 Age Calculator" },
  { to: "/prize-bond", label: "🎟️ Prize Bond Checker" },
  { to: "/earning-apps-review", label: "💸 Earning Apps Review" },
  { to: "/zakat-calculator", label: "🕌 Zakat Calculator" },
  { to: "/tools", label: "Tools" },
  { to: "/learn", label: "Learn" },
  { to: "/blog", label: "Blog" },
  { to: "/about-us", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/embed-tools", label: "🧩 Widgets" },
];

export function Layout({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openMobileMenu, setOpenMobileMenu] = useState<string | null>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const menuRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const isAdminRoute = pathname.startsWith("/admin");
  // Defer above-the-fold ad + GPT bootstrap until after the LCP window to
  // fix Search Console "LCP longer than 4s (mobile)" — GPT script and the
  // top-banner slot were competing with the hero paint on slow 4G devices.
  const [postLcp, setPostLcp] = useState(false);
  useEffect(() => {
    if (isAdminRoute || typeof window === "undefined") return;
    let done = false;
    const trigger = () => {
      if (done) return;
      done = true;
      setPostLcp(true);
    };
    // Whichever comes first: page load + short idle, first user interaction,
    // or a 3.5s hard cap so ads never get stuck off.
    const idle: (cb: () => void) => number =
      (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
        .requestIdleCallback ?? ((cb) => window.setTimeout(cb, 1));
    const onLoad = () => idle(() => window.setTimeout(trigger, 4500));
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad, { once: true });
    const hardCap = window.setTimeout(trigger, 7000);
    const opts = { once: true, passive: true } as AddEventListenerOptions;
    window.addEventListener("scroll", trigger, opts);
    window.addEventListener("pointerdown", trigger, opts);
    window.addEventListener("keydown", trigger, opts);
    return () => {
      window.clearTimeout(hardCap);
      window.removeEventListener("load", onLoad);
      window.removeEventListener("scroll", trigger);
      window.removeEventListener("pointerdown", trigger);
      window.removeEventListener("keydown", trigger);
    };
  }, [isAdminRoute]);

  useEffect(() => {
    if (!openMenu) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenMenu(null);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openMenu]);

  // ⌘K / Ctrl+K focuses the header search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (isAdminRoute || typeof window === "undefined") return;
    if (!postLcp) return;
    ensureGptScript();
    const fireAdRefresh = (phase: string) => {
      window.dispatchEvent(new CustomEvent("dh-ads-route-refresh", { detail: { path: pathname, phase } }));
    };
    fireAdRefresh("route-start");
    // Anchor + Interstitial (out-of-page) slots must be refreshed explicitly
    // on each SPA route change; display() alone won't re-serve them.
    refreshOutOfPageAds();
    const timers = [
      window.setTimeout(() => fireAdRefresh("route-settled"), 700),
      window.setTimeout(() => fireAdRefresh("route-late"), 1800),
    ];
    const onPageShow = () => fireAdRefresh("pageshow");
    const onVisibility = () => {
      if (document.visibilityState === "visible") fireAdRefresh("visible");
    };
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);
    const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag === "function") {
      gtag("config", "G-T04DVWJFNJ", {
        page_path: window.location.pathname + window.location.search,
        page_location: window.location.href,
      });
    }
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isAdminRoute, pathname, postLcp]);

  // NOTE: The previous forced full-page reload on every internal link click
  // (added for AdSense pageview counts) was breaking GAM interstitials —
  // the click that triggered the interstitial also reloaded the page, so
  // the ad auto-dismissed within ~0.5s. GAM natively tracks SPA route
  // changes via our `dh-ads-route-refresh` event, so the reload is no
  // longer needed. Keeping SPA navigation preserves interstitials until
  // the user closes them via the cross button.

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground overflow-x-clip max-w-[100vw]">
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/70 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-4 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 font-bold text-lg">
            <img
              src={logoUrl}
              alt={`${SITE_NAME} logo`}
              width={36}
              height={36}
              className="h-9 w-9 object-contain"
            />
            <span className="text-gold">{SITE_NAME}</span>
          </Link>

          <div className="hidden md:flex flex-1 max-w-md">
            <SearchAutocomplete variant="desktop" autoFocusRef={searchInputRef} onNavigate={() => setOpen(false)} />
          </div>

          <div ref={menuRef} className="flex items-center gap-2">
            <ThemeToggle />
            <button
              className="p-2 rounded-md hover:bg-muted"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {open ? <path d="M6 6l12 12M6 18L18 6" /> : <path d="M3 6h18M3 12h18M3 18h18" />}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile search — visible under logo row on small viewports */}
        <div className="md:hidden mx-auto max-w-6xl px-4 pb-3">
          <SearchAutocomplete variant="mobile" placeholder="Search rates, brands, bills…" onNavigate={() => setOpen(false)} />
        </div>

        {open && (
          <nav className="border-t border-border bg-background/95 backdrop-blur max-h-[80vh] overflow-y-auto">
            <div className="mx-auto max-w-6xl px-4 py-2 flex flex-col">
              {NAV.map((item) => {
                if (isDropdown(item)) {
                  const isOpen = openMobileMenu === item.label;
                  return (
                    <div key={item.label} className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => setOpenMobileMenu(isOpen ? null : item.label)}
                        className="px-3 py-3 rounded-md text-sm font-medium hover:bg-muted flex items-center justify-between text-left"
                        aria-expanded={isOpen}
                      >
                        <span>{item.label}</span>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                          aria-hidden
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-200 ${
                          isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="pl-4 border-l-2 border-primary/20 ml-3 my-1 flex flex-col">
                          {item.children.map((c) => (
                            <Link
                              key={c.to}
                              to={c.to}
                              onClick={() => {
                                setOpen(false);
                                setOpenMobileMenu(null);
                              }}
                              className="px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                            >
                              {c.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="px-3 py-3 rounded-md text-sm font-medium hover:bg-muted"
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </header>

      <main className="flex-1">
        {!isAdminRoute && postLcp && (
          <div className="mx-auto max-w-6xl px-4 pt-4">
            <DisplayAd placement="top-banner" className="mb-4" />
          </div>
        )}
        {children}
      </main>

      {!isAdminRoute && postLcp && (
        <div className="mx-auto max-w-6xl px-4 py-6">
          <DisplayAd placement="footer" />
        </div>
      )}

      <footer className="border-t border-border/60 bg-card/30 backdrop-blur mt-16">
        <FooterDirectory />
        <div className="mx-auto max-w-6xl px-4 py-12 grid gap-8 md:grid-cols-4 text-sm">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <img src={logoUrl} alt={`${SITE_NAME} logo`} width={28} height={28} className="h-7 w-7 object-contain" />
              <span className="font-bold text-base text-gold">{SITE_NAME}</span>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Live gold prices for Pakistan and India, updated every 10 minutes. Independent rates,
              transparent calculations, and free educational tools.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-[11px] text-muted-foreground">
              <span className="live-dot" /> Live data · refreshed every 10 minutes
            </div>
          </div>
          <div>
            <div className="font-semibold mb-3 text-foreground">Prices &amp; Tools</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/pakistan" className="hover:text-gold">Pakistan Rates</Link></li>
              <li><Link to="/india" className="hover:text-gold">India Rates</Link></li>
              <li><Link to="/tools" className="hover:text-gold">Gold Tools Suite</Link></li>
              <li><Link to="/learn" className="hover:text-gold">Learn Gold</Link></li>
              <li><Link to="/blog" className="hover:text-gold">Blog</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-3 text-foreground">Legal &amp; Compliance</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/about-us" className="hover:text-gold">About Us</Link></li>
              <li><Link to="/contact" className="hover:text-gold">Contact</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-gold" aria-label="Privacy Policy — full site policy page">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-gold">Terms of Service</Link></li>
              <li><Link to="/methodology" className="hover:text-gold">Pricing Methodology</Link></li>
              <li><Link to="/disclaimer" className="hover:text-gold">Financial Disclaimer</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border/60">
          <div className="mx-auto max-w-6xl px-4 py-5 text-xs text-muted-foreground flex flex-col md:flex-row gap-3 justify-between">
            <div>© {new Date().getFullYear()} {SITE_NAME}. All rights reserved.</div>
            <div className="max-w-xl md:text-right">
              <b>Financial Disclaimer:</b> Rates shown are indicative, derived from public market data, and may differ from your local jeweller. Not investment advice — always verify before buying or selling.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterDirectory() {
  const topPk = PAKISTAN_CITIES.slice(0, 6);
  const topIn = INDIA_CITIES.slice(0, 6);
  return (
    <nav aria-label="Site directory" className="border-b border-border/60 bg-card/20">
      <div className="mx-auto max-w-6xl px-4 py-8 grid gap-6 md:grid-cols-4 text-xs">
        <div>
          <div className="font-bold text-foreground mb-2">Pakistan gold</div>
          <ul className="space-y-1.5 text-muted-foreground">
            <li><Link to="/pakistan" className="hover:text-gold">Pakistan rates</Link></li>
            {topPk.map((c) => (
              <li key={c.slug}>
                <CityLink country="pakistan" city={c.slug} className="hover:text-gold">
                  {c.name} 22K & 24K
                </CityLink>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="font-bold text-foreground mb-2">India gold</div>
          <ul className="space-y-1.5 text-muted-foreground">
            <li><Link to="/india" className="hover:text-gold">India rates</Link></li>
            {topIn.map((c) => (
              <li key={c.slug}>
                <CityLink country="india" city={c.slug} className="hover:text-gold">
                  {c.name} 22K & 24K
                </CityLink>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="font-bold text-foreground mb-2">Online work</div>
          <ul className="space-y-1.5 text-muted-foreground">
            <li><Link to="/remote-jobs" className="hover:text-gold">Non-phone remote jobs</Link></li>
            <li><Link to="/remote-jobs/portals" className="hover:text-gold">Remote job portals</Link></li>
            <li><Link to="/app-testing" className="hover:text-gold">App testing guide</Link></li>
            <li><Link to="/app-testing/portals" className="hover:text-gold">Tester portals</Link></li>
            <li><Link to="/earning-apps-review" className="hover:text-gold">Earning apps review</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-bold text-foreground mb-2">Tools & developers</div>
          <ul className="space-y-1.5 text-muted-foreground">
            <li><Link to="/tools/gold-purity-converter" className="hover:text-gold">Gold purity converter</Link></li>
            <li><Link to="/tools/jewelry-making-estimator" className="hover:text-gold">Jewelry making estimator</Link></li>
            <li><Link to="/tools/historical-roi-calculator" className="hover:text-gold">Historical ROI calculator</Link></li>
            <li><Link to="/developer/gold-rate-api" className="hover:text-gold">Free gold rate API</Link></li>
          </ul>
        </div>
      </div>
      <FooterCrawlerLinks />
    </nav>
  );
}